import { useEffect, useRef } from 'react'
import { fetchUploadStatuses } from './api'

const BACKOFF_INTERVALS = [2000, 4000, 8000, 15000]
const UPLOAD_PROCESSING_TIMEOUT_MS = 60000

export function useUploadPoller(
  vendorToken,
  builderSessionId,
  pendingIds,
  onUpdate,
  builderDraftPublicId,
  timeoutIds = pendingIds
) {
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate
  const pendingKey = pendingIds.map(String).sort().join(',')
  const timeoutKey = timeoutIds.map(String).sort().join(',')

  useEffect(() => {
    if (pendingIds.length === 0) return

    let active = true
    let timer
    let attempt = 0
    let timeoutReported = false
    const startedAt = Date.now()
    const polledIds = [...pendingIds]
    const polledTimeoutIds = [...timeoutIds]

    const poll = async () => {
      if (!active) return

      if (!timeoutReported && Date.now() - startedAt > UPLOAD_PROCESSING_TIMEOUT_MS) {
        timeoutReported = true
        if (polledTimeoutIds.length > 0) {
          onUpdateRef.current(polledTimeoutIds, 'timeout')
        }
        if (polledTimeoutIds.length === polledIds.length) return
      }

      try {
        const uploads = builderDraftPublicId
          ? await fetchUploadStatuses(vendorToken, builderSessionId, polledIds, builderDraftPublicId)
          : await fetchUploadStatuses(vendorToken, builderSessionId, polledIds)
        if (!active) return
        onUpdateRef.current(uploads)
      } catch (e) {
        // network error — continue polling
      }

      if (active) {
        const interval = BACKOFF_INTERVALS[Math.min(attempt, BACKOFF_INTERVALS.length - 1)]
        attempt += 1
        timer = setTimeout(poll, interval)
      }
    }

    timer = setTimeout(poll, BACKOFF_INTERVALS[0])

    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [builderDraftPublicId, builderSessionId, pendingKey, timeoutKey, vendorToken])

  return null
}
